'use client'

import { type JSX, useEffect, useRef, useState } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { Container } from '../design-system'
import CalendlyButton from './calendly-popup'
import { DEMO_URL } from './links'

/**
 * The marketing header: logo, primary navigation and the demo button, rendered once by the (marketing)
 * layout. It reads the path itself with `usePathname()` for `aria-current`, so the layout stays a
 * server component and the pages pass nothing in.
 *
 * Below 900px the links move into a drawer, which is a native modal <dialog>. `showModal()` is what
 * makes the page behind it inert, moves focus in, closes on Escape and hands focus back to the toggle
 * on close, so none of that is written here. The dialog covers the viewport and repeats the bar
 * (logo, close, demo) at the header's exact geometry, because a modal dialog inerts the real header
 * too — its toggle could not close anything. The scroll lock and the fade are CSS.
 *
 * Every value is measured on https://www.sonicjobs.com/ (2026-09-23), 1728px and 390px, both themes;
 * the before/after of each normalisation is D1-D20 in the deltas doc
 * (docs/superpowers/specs/2026-09-22-s20-825-marketing-design-system-deltas.md, section 3.2). `max-[900px]`/`min-[900px]` split
 * at production's `max-width: 899px`, `max-[761px]` at its `max-width: 760px` — Tailwind's `max-*`
 * is `width < N`.
 */

const SECTIONS = [
  { href: '/#suite', label: 'The GPT Suite' },
  { href: '/#visibility', label: 'Employer GPT Visibility' },
  { href: '/#careergpt', label: 'CareerGPT' },
  { href: '/#insights', label: 'Insights' },
]

const PAGES = [
  { href: '/career-gpt', label: 'Try CareerGPT' },
  { href: '/jobs', label: 'Jobs' },
  { href: '/blogs', label: 'Blog' },
]

// Full literal class strings per placement, so Tailwind's scanner sees every class.
const STYLES = {
  // The bar: 36px pills, and "Why SonicJobs" opens its menu on hover or focus.
  bar: {
    group: 'group/menu relative flex items-center',
    item: 'flex items-center min-h-[36px] px-[16px] rounded-full font-medium text-base leading-none text-ink-secondary no-underline transition-colors duration-[160ms] hover:text-ink hover:bg-fill-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    caret:
      'ml-[6px] -mr-px size-[14px] transition-transform duration-[160ms] group-hover/menu:translate-y-[2px] group-focus-within/menu:translate-y-[2px]',
    menu: 'invisible absolute top-[calc(100%+1px)] -left-[16px] flex w-[246px] -translate-y-[8px] flex-col rounded-xl border border-border bg-surface-raised p-[8px] opacity-0 shadow-overlay transition-[opacity,translate,visibility] duration-[160ms] group-hover/menu:visible group-hover/menu:translate-y-0 group-hover/menu:opacity-100 group-focus-within/menu:visible group-focus-within/menu:translate-y-0 group-focus-within/menu:opacity-100 motion-reduce:transition-none',
    sub: 'flex min-h-[44px] items-center rounded-md px-[12px] font-medium text-sm leading-normal text-ink-secondary no-underline transition-colors duration-[160ms] hover:bg-fill-strong hover:text-accent focus-visible:bg-fill-strong focus-visible:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  },
  // The drawer: 58px rows, and the section links always shown in a raised panel. `text-[16px]` has
  // no token — it is the drawer's body size, the value the 2xl button uses too.
  drawer: {
    group: 'flex flex-col',
    item: 'flex items-center justify-between min-h-[58px] rounded-md font-medium text-[16px] leading-normal text-ink-secondary no-underline transition-colors duration-[160ms] hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    caret: '-mr-px size-[14px]',
    menu: 'flex flex-col rounded-xl bg-surface-raised pt-[4px] pr-0 pb-[12px] pl-[18px]',
    sub: 'flex min-h-[44px] items-center rounded-md font-medium text-sm leading-normal text-ink-secondary no-underline transition-colors duration-[160ms] hover:text-accent focus-visible:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  },
}

type NavLinksProps = { placement: keyof typeof STYLES; onNavigate?: () => void }

/** The link list, shared by the bar and the drawer so the two cannot drift apart. */
function NavLinks({ placement, onNavigate }: NavLinksProps): JSX.Element {
  const style = STYLES[placement]
  const pathname = usePathname()

  return (
    <>
      <div className={style.group}>
        <Link href="/#shift" className={style.item} onClick={onNavigate}>
          Why SonicJobs
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={style.caret}>
            <path
              fillRule="evenodd"
              d="M4.293 8.293a1 1 0 0 1 1.414 0L12 14.586l6.293-6.293a1 1 0 1 1 1.414 1.414l-7 7a1 1 0 0 1-1.414 0l-7-7a1 1 0 0 1 0-1.414Z"
              clipRule="evenodd"
            />
          </svg>
        </Link>
        <div className={style.menu}>
          {SECTIONS.map(({ href, label }) => (
            <Link key={href} href={href} className={style.sub} onClick={onNavigate}>
              {label}
            </Link>
          ))}
        </div>
      </div>

      {PAGES.map(({ href, label }) => (
        <Link
          key={href}
          href={href}
          aria-current={href === pathname ? 'page' : undefined}
          className={style.item}
          onClick={onNavigate}
        >
          {label}
        </Link>
      ))}
    </>
  )
}

// Preloaded in both places it renders: the header's is above the fold, and the drawer's is the same
// URL, which React's preload hoisting deduplicates.
function Logo(): JSX.Element {
  return (
    <Link href="/" aria-label="SonicJobs home" className="flex min-h-[44px] shrink-0 items-center">
      <Image
        src="/assets/legacy/sonicjobs-logo-original.png"
        alt="SonicJobs"
        width={3420}
        height={802}
        sizes="124px"
        preload
        className="h-auto w-[124px] brightness-0 grayscale max-[900px]:w-[118px] dark:invert"
      />
    </Link>
  )
}

// `onClick` lets the drawer close itself first: a modal <dialog> sits in the top layer, so Calendly's
// popup (appended to <body>) would otherwise open underneath it, inert.
function DemoButton({ onClick }: { onClick?: () => void }): JSX.Element {
  // Below 900px the button is a fixed 111px, measured on production; the primitive leaves width to us.
  return (
    <div className="flex w-[111px] shrink-0 items-center min-[900px]:ml-auto min-[900px]:w-auto">
      <CalendlyButton
        color="primary"
        size="lg"
        href={DEMO_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="w-[111px] min-[900px]:w-auto"
        onClick={onClick}
      >
        Get a demo
      </CalendlyButton>
    </div>
  )
}

const BAR = 'flex h-full items-center gap-[18px] max-[900px]:gap-[10px] max-[381px]:gap-[6px]'
const TOGGLE =
  'ml-auto flex size-[44px] shrink-0 cursor-pointer flex-col items-center justify-center gap-[6px] rounded-full border-0 bg-fill-subtle p-0 font-sans text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

export default function MarketingNav(): JSX.Element {
  const [open, setOpen] = useState(false)
  const drawer = useRef<HTMLDialogElement>(null)

  const openDrawer = (): void => {
    drawer.current?.showModal()
    setOpen(true)
  }
  const closeDrawer = (): void => drawer.current?.close()

  // The drawer is only drawn below 900px, but a modal dialog hidden by CSS would still leave the page
  // inert, so crossing the breakpoint closes it. An effect rather than useSyncExternalStore: the job
  // is to act on the change, not to render from the value, and the store would still need an effect.
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 900px)')
    const onChange = (event: MediaQueryListEvent): void => {
      if (event.matches) drawer.current?.close()
    }
    wide.addEventListener('change', onChange)

    return (): void => wide.removeEventListener('change', onChange)
  }, [])

  return (
    <>
      {/* z 190 sits above the sticky header's 180; below it, the focused link is painted over. */}
      <a
        href="#main-content"
        className="fixed top-[10px] left-[10px] z-[190] -translate-y-[180%] rounded-md bg-ink px-[14px] py-[10px] font-semibold text-[16px] leading-normal text-canvas no-underline transition-transform duration-[160ms] focus:translate-y-0 motion-reduce:transition-none"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-[180] h-[64px] bg-surface-overlay backdrop-blur-[18px] backdrop-saturate-[1.2] max-[761px]:h-[60px]">
        <Container className={BAR}>
          <Logo />

          <button
            type="button"
            aria-expanded={open}
            aria-controls="primary-navigation"
            onClick={openDrawer}
            className={`${TOGGLE} min-[900px]:hidden`}
          >
            <span aria-hidden="true" className="block h-px w-[18px] bg-ink" />
            <span aria-hidden="true" className="block h-px w-[18px] bg-ink" />
            <span className="sr-only">Navigation</span>
          </button>

          <nav aria-label="Primary" className="hidden items-center min-[900px]:flex">
            <NavLinks placement="bar" />
          </nav>

          <DemoButton />
        </Container>
      </header>

      {/* `[body:has(&:modal)]:overflow-hidden` is the scroll lock: it targets <body> while this dialog
          is modal. The fade runs both ways through `@starting-style` and discrete transitions; reduced
          motion drops them. */}
      <dialog
        ref={drawer}
        id="primary-navigation"
        aria-label="Primary navigation"
        onClose={(): void => setOpen(false)}
        className="group/drawer fixed inset-0 m-0 h-dvh max-h-none w-full max-w-none overflow-hidden border-0 bg-transparent p-0 text-ink opacity-0 transition-[opacity,display,overlay] transition-discrete duration-[160ms] backdrop:bg-transparent open:opacity-100 starting:open:opacity-0 motion-reduce:transition-none [body:has(&:modal)]:overflow-hidden"
      >
        <div className="h-[64px] bg-surface-overlay backdrop-blur-[18px] backdrop-saturate-[1.2] max-[761px]:h-[60px]">
          <Container className={BAR}>
            <Logo />
            <button type="button" onClick={closeDrawer} className={TOGGLE}>
              <span aria-hidden="true" className="block h-px w-[18px] translate-y-[3.5px] rotate-45 bg-ink" />
              <span aria-hidden="true" className="block h-px w-[18px] -translate-y-[3.5px] -rotate-45 bg-ink" />
              <span className="sr-only">Close menu</span>
            </button>
            <DemoButton onClick={closeDrawer} />
          </Container>
        </div>

        <nav className="flex h-[calc(100dvh-64px)] -translate-y-[8px] flex-col overflow-y-auto overscroll-contain border-t border-border-strong bg-canvas px-[16px] pt-[20px] pb-[36px] shadow-drawer transition-transform duration-[160ms] group-open/drawer:translate-y-0 max-[761px]:h-[calc(100dvh-60px)] starting:group-open/drawer:-translate-y-[8px] motion-reduce:transition-none">
          <NavLinks placement="drawer" onNavigate={closeDrawer} />
        </nav>
      </dialog>
    </>
  )
}
